import { ApplicationPayment, Payment, ApplicationCreditAuthorization, AuthorizationResponse } from "../../payments/models/paymentItem.model";


export enum ApplicationType {
    Stratigraphic = "Stratigraphic",
	ComplianceReporting = "Compliance Reporting",
	BulkNAD83Update = "Bulk NAD83 Update"
}


export interface WorkflowData {
    id: number;
    workflowType: ApplicationType;
    organizationId: string;
    applicationNumber: string;
    createdById: string;
    applicationState: ApplicationState;
    isReturned: boolean;
    createdDate: Date;
    lastModifiedDate: Date;
    lastModifiedBy: string;
    dateSubmitted?: Date;
    workflowMetadata?: WorkflowMetadata;
    flowHistories?: ApplicationFlowHistory[];
    workflowFieldDatas?: WorkflowFieldData[];
    workflowNotes?: WorkflowNote[];
   
}

export interface StratigraphicApplicationMetadata {
    id: number;
    existingAPINumber: string;
    county: string;
    countyNumber: string;
    township: string;
    wellName: string;
    wellNumber: string;
    applicationCompleteDate?: Date;
    paymentReceivedDate?: Date;
    dueDate?: Date;
    isExpedited?: boolean;
    applicationType: string;
    urbanization: string;
    surfaceLatitude?: number;
    surfaceLongitude?: number;
    targetLatitude?: number;
    targetLongitude?: number;
    slant: string;
    proposedFormations: string;
    marcellusShale?: boolean;
    uticaShale?: boolean;
    proposedTotalDepth?: number;
    watershed: string;
    stateRoyaltyInterest?: boolean;
    emergencyContactName: string;
    emergencyContactPhone: string;
    emergencyContactEmail: string;
}

export interface ComplianceReportApplicationMetadata {
    id: number;
    APINumber: string;    
    wellName: string;
    wellNumber: string;    
    dueDate?: Date;
}

export interface WorkflowMetadata {
    id: number;
    identifierType: string;
    applicationNumber: string;
    workflowName: string;
    createdById: string;
    createdByUserName: string;
    currentState: string;
    organizationId: string;
    organizationName: string;
    flowIdentifier: string;
    timestamp: Date;
    activeWorkItemId?: string;
    assignedUserId?: string;
    assignedUserName?: string;
    assignedGroupId?: string;
    assignedGroupName?: string;
    isInputState: boolean;
    isReviewState: boolean;
    isEndState: boolean;
    isStartState: boolean;
    isReturned: boolean;
    stratigraphicApplicationMetadata: StratigraphicApplicationMetadata;
    onBehalf?: boolean;
    reportType?: string;
}

export interface ApplicationEventType {
    id: number;
    name: string;
}

export interface ApplicationFlowHistory {
    id: number;
    workflowId: string;
    eventTypeId: number;
    eventDate: Date;
    userId: string;
    userName: string;
    organizationId: string;
    organizationName: string;
    workOrderId?: string;
}

export interface WorkflowFieldFile {
    id: number;
    fieldId: number;
    blobUri: string;
    fileName: string;
    fileType: string;
    documentType: string;
    uploadedByUserId: string;
    uploadedByOrganizationId: string;
}

export interface WorkflowFieldData {
    id: number;
    workflowId: number;
    formControlName: string;
    stringData?: string;
    dateData?: Date;
    numericData?: number;
    booleanData?: boolean;
    workflowNotes?: WorkflowNote[];
    workflowFieldFiles?: WorkflowFieldFile[];
    applicationCreditAuthorization?: ApplicationCreditAuthorization;
    applicationPayments?: ApplicationPayment[];
  
}

export interface WorkflowNote {
    id: string;
    workflowId: number;
    fieldId?: number;
    parentNoteId?: string;
    createdDate: Date;
    createdBy: string;
    createdById: string;
    text: string;
    isDeleted: boolean;
    referenceUri?: string;
    retentionPolicyId?: string;
    retentionPolicyName?: string;
    termTicks?: number;
    notePriorityId: number;
    noteTypeId: number;
    childNotes?: WorkflowNote[];
}

export interface WorkflowNoteWithChildren extends WorkflowNote {
    childNotes?: WorkflowNote[];

}

export enum ApplicationState {
    Start = "Start",
    Input = "Input",
    Corrections = "Returned for Corrections",
    Withdrawn = "Withdrawn",
    Denied = "Denied",
    InitialReview = "Initial Review",
    VerifyCompletion = "Verify Completion",
    InitialApproval = "Initial Approval",
    FinalApproval = "Final Approval",
    Approved = "Approved",
    ReturnedToInitialApproval = "Returned to Initial Approval",
    ReturnedToInitialReview = "Returned to Initial Review"
}

export interface InitiateWorkflowRequest {
    organizationId: string;
    applicationType: ApplicationType
    onBehalf?: boolean;
}

export interface MetadataSearchSpec {
    ids?: number[];
    applicationStates?: string[];
    applicationTypes?: string[];
    organizationId?: string;
    onBehalf?: boolean;
}

export interface WorkflowSearchSpec {
	ids?: string[];
	applicationNumbers?: string[];
	applicationTypes?: ApplicationType[];
}

export enum ApplicationSteps {
    VerifyOwner = 0,
    PermitType = 1,
    WellInformation = 2,
    WaterSource = 3,
    Contacts = 4,
    WellSiteAccess = 5,
    WellConstruction = 6,
    Documentation = 7,
    Signature = 8,
    Payment = 9,
    Review = 10, //end stratigraphic
    SelectWell = 11, //begin as drilled plat compliance report
    Unitization = 12,
    PlatLocation = 13    
}

export enum PaymentSteps {
    Itemization = 0,
    PaymentType = 1,
    Invoicing = 2
}


export interface ApplicationPaymentParameters {
    applicationId: number
    applicationNumber: string;
    applicationType: ApplicationType;
    forceRecalculate: boolean;
    newWell: boolean;
    permitType: string;
    wellType: string;
    wellTypeDescription: string;
    fluidProposed: string;
    expeditePermit: boolean;
    unitMPn: string;
    urbanArea: string;
}

export interface ApplicationStateChangeRequest {
    applicationId: number;
    currentState: ApplicationState;
    requestedState: ApplicationState;
}

export interface ApplicationPermitResult {
    id: string;
    data: any;
    documentId: string;
    queuedDocumentId: string;
}

export interface GeneratePermitRequest {
    workflowId: number;
    applicationNumber: string;
    uploadDocument: boolean;
}

export interface CommitFilesRequest {
    applicationNumber: string;
    confidential: boolean
}

export interface BulkNAD83Data {
	apiNumber: string,
	countyCode: string,
	countyName: string,
	fromLatitude83?: number,
	fromLongitude83?: number,
	latitude83: number,
	longitude83: number,
	line: number;
	sourceOfLocation: string
}